import React, { useState, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Search, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const HistoryView = ({ entries, onDeleteEntry }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCommessa, setFilterCommessa] = useState('all');

  // Get unique commesse from entries
  const uniqueCommesse = useMemo(() => {
    const commesse = new Set(entries.map(e => e.commessa_name));
    return Array.from(commesse).sort();
  }, [entries]);

  // Filter entries
  const filteredEntries = useMemo(() => {
    return entries.filter(entry => {
      const matchesSearch = searchQuery === '' ||
        entry.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        entry.commessa_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (entry.project_name && entry.project_name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesCommessa = filterCommessa === 'all' || entry.commessa_name === filterCommessa;
      
      return matchesSearch && matchesCommessa;
    });
  }, [entries, searchQuery, filterCommessa]);

  // Group entries by date
  const groupedEntries = useMemo(() => {
    const groups = {};
    filteredEntries.forEach(entry => {
      if (!groups[entry.date]) {
        groups[entry.date] = [];
      }
      groups[entry.date].push(entry);
    });
    return groups;
  }, [filteredEntries]);

  const sortedDates = Object.keys(groupedEntries).sort((a, b) => b.localeCompare(a));

  const handleDelete = async (entryId) => {
    await onDeleteEntry(entryId);
  };

  const totalHours = filteredEntries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-6">
        <h2 className="font-display text-3xl font-bold gold-text mb-2">Storico</h2>
        <p className="text-muted-foreground">Tutte le tue registrazioni.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Cerca..."
            className="input-mafia pl-10"
            data-testid="search-input"
          />
        </div>
        <Select value={filterCommessa} onValueChange={setFilterCommessa}>
          <SelectTrigger className="input-mafia w-full md:w-48" data-testid="filter-commessa-select">
            <SelectValue placeholder="Tutte le commesse" />
          </SelectTrigger>
          <SelectContent className="bg-card border-border">
            <SelectItem value="all">Tutte le commesse</SelectItem>
            {uniqueCommesse.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Stats */}
      <div className="flex items-center justify-between mb-4 p-4 bg-muted/30 rounded-lg">
        <span className="text-muted-foreground">
          {filteredEntries.length} registrazion{filteredEntries.length === 1 ? 'e' : 'i'}
        </span>
        <span className="font-semibold">
          Totale: <span className="gold-text">{totalHours.toFixed(1)}h</span>
        </span>
      </div>

      {/* Entries list */}
      <div className="space-y-6">
        {sortedDates.map((date) => {
          const dayEntries = groupedEntries[date];
          const dayTotal = dayEntries.reduce((sum, e) => sum + e.hours, 0);
          const formattedDate = format(new Date(date), 'EEEE d MMMM yyyy', { locale: it });

          return (
            <div key={date} className="animate-fade-in">
              {/* Date header */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span className="font-medium capitalize">{formattedDate}</span>
                </div>
                <Badge variant="outline" className="gold-text border-primary">
                  {dayTotal}h
                </Badge>
              </div>

              {/* Day entries */}
              <Card className="card-mafia">
                <CardContent className="p-0 divide-y divide-border">
                  {dayEntries.map((entry) => (
                    <div
                      key={entry.id}
                      className="p-4 flex items-start justify-between gap-4 hover:bg-muted/30 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <Badge>{entry.commessa_name}</Badge>
                          {entry.project_name && (
                            <Badge variant="secondary">{entry.project_name}</Badge>
                          )}
                          <span className="text-xs text-muted-foreground font-mono">
                            {entry.commessa_code}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{entry.description}</p>
                      </div>
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="flex items-center gap-1 gold-text font-semibold">
                          <Clock className="w-4 h-4" />
                          {entry.hours}h
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(entry.id)}
                          className="text-muted-foreground hover:text-destructive"
                          data-testid={`delete-entry-${entry.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          );
        })}

        {sortedDates.length === 0 && (
          <div className="text-center py-16">
            <p className="text-muted-foreground">Nessuna registrazione trovata</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default HistoryView;
